import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { GetCoopListService } from './api-services/get-coop-list.service';
import { CoopCodeComboBoxModel } from './coop-code-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { CoopModel } from './coop.model';

/**
 * 共通部品 会員生協コンボボックスを制御するクラス
 *
 * @export CodeComboBoxComponent
 * @class CodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-coop-code-combo-box',
  templateUrl: 'coop-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CoopCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetCoopListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetCoopListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }]
  ,
})

export class CoopCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Output() public focus: EventEmitter<any> = new EventEmitter<any>();

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() allCoop: boolean = <boolean>true;                                    // 全生協表示フラグ
  @Input() initAutoLoad: boolean = <boolean>true;                               // オードロードフラグ
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  public selectedCoopCode: string = <string>'';	                                // 生協コード
  public selectedCoopName: string = <string>'';	                                // 生協名
  public selectedIndex: number = <number>-1;                                    // 選択した番号

  public model: CoopCodeComboBoxModel = new CoopCodeComboBoxModel();

  /**
   * Creates an instance of codeComboBox.
   * @memberof codeComboBox
   * @param {getCodeListService} GetCodeListService コードリストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getCoopListService: GetCoopListService,
  ) { }

  // ValueAccessor
  public disabled: boolean;
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedCoopCode) {
      this.selectedCoopCode = text;
      this.setCoopCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof CoopCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof CoopCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getCoopListService.execute(this.model);
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof CoopCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'allCoop':
          // 全生協表示表示フラグ
          this.model.allCoop = changes.allCoop.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof CoopCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.selectedCoopCode = null;                                               // 生協コード
    this.selectedCoopName = null;                                               // 生協名
    this.selectedIndex = null;                                                  // 選択した番号
    this.model = null;
  }

  /**
   * クリア
   *
   * @memberof CoopCodeComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedCoopCode = null;                                               // 生協コード
    this.selectedCoopName = null;                                               // 生協名
    this.selectedIndex = null;                                                  // 選択した番号
    this.model = new CoopCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択生協Nameを切り替える
   *
   * @param {string} coopCode 生協コード
   * @memberof CoopCodeComboBoxComponent
   */
  public setCoopCode(coopCode: string): void {
    this.model.setCoopCode = coopCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCoopCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof CoopCodeComboBoxComponent
   */
  public updateSelectedCoopCode(): void {
    const coopList: CoopModel[] = this.model.coopList;
    for (let i = Number(0); i < coopList.length; i++) {
      if (coopList[i].CoopCode === this.model.setCoopCode) {
        this.selectedIndex = i;
      }
    }

    if (this.afterFocus != null) {
      if (this.afterFocus instanceof HTMLElement) {
        this.afterFocus.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.afterFocus.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }
    }
    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof CoopCodeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedCoopCode = null;                                             // 生協コード
      this.selectedCoopName = null;                                             // 生協名

      return;
    }

    this.selectedCoopCode = this.model.coopList[this.selectedIndex].CoopCode;
    this.selectedCoopName = this.model.coopList[this.selectedIndex].CoopName;

    // 同じ意味合いのafterFocusがあればinitFocusItemに入れる
    if (this.afterFocus != null) {
      this.initFocusItem = this.afterFocus;
    }

    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }
}
